"use client"

import { useState } from "react"
import { Share2, Check, Link as LinkIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { contentAnalytics } from "@/lib/analytics"

/**
 * Share control pinned to the top-right corner of a hero's dark-glass meta card.
 * Shared by the blog hero + both guide heroes.
 *
 * - Absolutely positioned, so it costs the card zero extra height (the card is
 *   flex-wrap; a normal flex child would drop to its own line on narrow phones
 *   and add ~44px back). The card only needs `relative` — deliberately NO
 *   reserved right padding: the card's width is set by its widest row (the
 *   date/reading-time line), which leaves ~50px of natural slack beside the
 *   shorter author row for the icon to sit in. Reserving padding instead cost
 *   the date row 42px of width for no benefit.
 * - Bare icon, no filled disc. globals.css forces min-height/width 44px on every
 *   button under 768px (WCAG touch target), so the hit area stays 44px while
 *   only the 20px glyph is visible — shrinking the button itself would fight
 *   that a11y rule.
 * - White, because the default `text-gray-500` was effectively invisible
 *   against a photo hero, which is why nobody could find this control.
 * - Mobile only — desktop keeps BlogShareButtons in the hero's top row.
 */
export const HERO_CARD_SHARE_CLASS =
  "absolute right-0 top-0 grid h-11 w-11 place-items-center p-0 mr-0 text-white transition hover:text-white/70 active:scale-95 md:hidden"

interface MobileShareButtonProps {
  title: string
  url?: string
  description?: string
  className?: string
  articleSlug?: string
}

export function MobileShareButton({
  title,
  url,
  description,
  className,
  articleSlug
}: MobileShareButtonProps) {
  const [showCopied, setShowCopied] = useState(false)

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '')
  // Extract slug from URL if not provided
  const effectiveSlug = articleSlug || (typeof window !== 'undefined' ? window.location.pathname.split('/').pop() || 'unknown' : 'unknown')

  // Check if native share is supported
  const canNativeShare = typeof navigator !== 'undefined' && 'share' in navigator

  const handleShare = async () => {
    if (canNativeShare) {
      try {
        await navigator.share({
          title,
          text: description,
          url: shareUrl,
        })
        // Track successful native share
        contentAnalytics.articleShared(effectiveSlug, 'native', true)
      } catch (error) {
        // User cancelled or share failed
        if ((error as Error).name === 'AbortError') {
          // User cancelled - don't track as failure
          return
        }
        // Share failed - fall back to copy
        handleCopyFallback()
      }
    } else {
      // Fallback: copy to clipboard
      handleCopyFallback()
    }
  }

  const handleCopyFallback = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setShowCopied(true)
      setTimeout(() => setShowCopied(false), 2000)
      // Track successful clipboard copy
      contentAnalytics.articleShared(effectiveSlug, 'clipboard', true)
    } catch (error) {
      console.error('Failed to copy:', error)
      // Track failed share attempt
      contentAnalytics.articleShared(effectiveSlug, 'clipboard', false)
    }
  }

  return (
    <button
      onClick={handleShare}
      className={cn(
        "flex items-center justify-center",
        "p-2 -mr-2",
        "text-gray-500 hover:text-green-600",
        "active:scale-95",
        "transition-all duration-200",
        className
      )}
      aria-label={showCopied ? "Link copied!" : "Share this article"}
    >
      {showCopied ? (
        <Check className="w-5 h-5 text-green-600" />
      ) : (
        <Share2 className="w-5 h-5" />
      )}
    </button>
  )
}
